import { isEqual } from 'lodash-es';
import { useMemo, useRef } from 'react';
import { map } from 'rxjs';
import type { SubscriptionResponse } from '../types';
import {
  useSubscription,
  type UseSubscriptionOptions,
  type UseSubscriptionRequest,
  type UseSubscriptionReturn,
} from './useSubscription';

/**
 * This hook wraps useSubscription and just does one thing: makes the returned `data` observable directly scoped to the request object sent to it.
 *
 * This ensures that subscribing to the returned data observable will only ever give you data from the _current request_. Compared to the
 * normal useSubscription where there are timing windows where you could subscribe and get data returned related to the previous request.
 *
 * When your request changes, defined by lodash.isEqual, the new request is passed to useSubscription, and at the same time
 * the returned `data` observable is re-created.
 *
 * `replay`, which is defaulted to true for useSubscription, is omitted from the options parameter and hard-coded to false for all users
 * of this hook. The whole point of having an request-scoped data observable returned is that when the request changes, we want to wipe all traces of
 * records coming from the previous request stream. Hence, replay does not make sense being true.
 *
 * TODO: This should become an option built into useSubscription, which would likely simplify the undesirable ref updating in here
 */
export const useRequestScopedSubscription = <TData = any>(
  request: UseSubscriptionRequest,
  options?: Omit<UseSubscriptionOptions, 'replay'>
): UseSubscriptionReturn<SubscriptionResponse<TData>> => {
  // Add in some logic here to only actually re-create the observable when there's an actual difference in the passed in requests

  // Assigning refs during rendering is a big no-no in react, but we need to do that here.
  // We *have to* re-create the outputObservable on _the same tick_ as the request is changed
  // Otherwise, a consumer has one tick where they re-subscribe to the outputObservable we are about to destroy.
  // This behavior is covered in the test in the functional example.
  const requestRef = useRef(request);
  if (!isEqual(requestRef.current, request)) {
    requestRef.current = request;
  }

  const { data, ...subscription } = useSubscription(requestRef.current, { replay: false, ...options });

  const outputObservable = useMemo(() => {
    // dummy map just to create a new observable (pipe creates a new observable)
    return data.pipe(map(d => d));

    // requestRef.current is intentionally here to ensure that the observable is re-created when the request changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, requestRef.current]);

  return { ...subscription, data: outputObservable };
};
